import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../composants/Button/Button';
import { ReactComponent as LogoIcon } from "../../assets/logo/logo-centralis-gris.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/view-more.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/visibility-off.svg";
import CGVAccept from '../../composants/CGVAccept/CGVAccept';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import './Register.css';

interface Organisme {
  id: number;
  nom: string;
}

interface IgnSuggestion {
  label: string;
  houseNumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
}

const Register: React.FC = () => {

  const [adresseInput, setAdresseInput] = useState('');
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [isAcheteur, setIsAcheteur] = useState<null | boolean>(null);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    confirm_email: '',
    password: '',
    password_confirmation: '',
    organisme: '',
    telephone: '',
    departement: '',
    code_postal: '',
    ville: '',
    prenom: '',
    acceptCGV: false,
    numero_voie: '',
    type_voie: '',
    nom_voie: '',
    siret: '',
  });

  const [prestataireSouscription, setPrestataireSouscription] = useState({
    entite: '',
    email: ''
  });

  const [error, setError] = useState('');
  const [cgvError, setCgvError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [organismeExists, setOrganismeExists] = useState<boolean | null>(null);
  const [organismeOptions, setOrganismeOptions] = useState<Organisme[]>([]);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [passwordValidation, setPasswordValidation] = useState({
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    isLongEnough: false,
    notEmail: true
  });
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [telError, setTelError] = useState('');
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [isOrganismeSearchLoading, setIsOrganismeSearchLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Effet pour rechercher l'organisme
  useEffect(() => {
    if (!formData.organisme || formData.organisme.length < 2) {
      setOrganismeOptions([]);
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeoutId = setTimeout(() => {
      searchOrganisme(formData.organisme);
    }, 500);

    setSearchTimeout(timeoutId);

    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [formData.organisme]);

  // Vérification de la correspondance des mots de passe
  useEffect(() => {
    setPasswordsMatch(
      formData.password === formData.password_confirmation &&
      formData.password !== ''
    );
  }, [formData.password, formData.password_confirmation]);

  // Effet pour l'autocomplétion d'adresse IGN
  useEffect(() => {
    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();

      fetch(`https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(adresseInput)}&index=StreetAddress&limit=8`,
        { signal: controller.signal })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(json => {
          console.log('IGN completion', json);
          if (json && json.results) {
            // Transformation des résultats dans le format IgnSuggestion
            const suggestions = json.results.map((result: any) => ({
              label: result.fulltext,
              houseNumber: result.kind === 'housenumber' ? result.fulltext.split(' ')[0] : '',
              street: result.street,
              postcode: result.zipcode,
              city: result.city
            }));
            console.log('Suggestions transformées:', suggestions);
            setAdresseSuggestions(suggestions);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Erreur lors de la récupération des adresses:', error);
          }
        });

      return () => controller.abort();
    }, 300);
  }, [adresseInput]);

  // Fonction utilitaire pour détecter le type de voie
  const detectTypeVoie = (street?: string): string => {
    if (!street) return '';
    const s = street.toLowerCase();
    if (s.startsWith('rue')) return 'rue';
    if (s.startsWith('avenue')) return 'avenue';
    if (s.startsWith('boulevard')) return 'boulevard';
    if (s.startsWith('impasse')) return 'impasse';
    if (s.startsWith('allée')) return 'allée';
    if (s.startsWith('place')) return 'place';
    return '';
  };

  const searchOrganisme = async (query: string) => {
    setIsOrganismeSearchLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/organismes/autocomplete/${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      const data = await response.json();

      if (data.status === 'success' && Array.isArray(data.data)) {
        setOrganismeOptions(data.data);
        setOrganismeExists(data.data.length > 0);
      } else {
        setOrganismeOptions([]);
        setOrganismeExists(false);
      }
    } catch (error) {
      console.error('Erreur de recherche organisme:', error);
      setOrganismeOptions([]);
      setOrganismeExists(false);
    } finally {
      setIsOrganismeSearchLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCgvError('');
    setIsLoading(true);

    try {
      // Validation initiale
      if (isAcheteur === null) {
        throw new Error("Veuillez répondre à la question 'Êtes-vous un acheteur ?'");
      }

      if (!formData.acceptCGV) {
        // Ouvrir le modal des CGV au lieu de lancer une erreur
        setIsCGVModalOpen(true);
        setCgvError("Vous devez lire et accepter les Conditions Générales pour vous inscrire.");
        setIsLoading(false);
        return;
      }

      if (isAcheteur) {
        // Logique pour les acheteurs
        if (organismeExists) {
          // Cas organisme existant

          // 1. Création de l'utilisateur
          const registerResponse = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
              password_confirmation: formData.password_confirmation,
              telephone: formData.telephone,
              role: 'acheteur'
            }),
          });

          const registerData = await registerResponse.json();

          if (registerData.status !== 'success') {
            if (registerData.errors) {
              const firstErrorKey = Object.keys(registerData.errors)[0];
              if (firstErrorKey) {
                throw new Error(registerData.errors[firstErrorKey][0] || registerData.message);
              }
            }
            throw new Error(registerData.message || "Erreur lors de l'inscription");
          }

          // 2. Récupération de l'ID organisme
          const organismeSearchResponse = await fetch(`${process.env.REACT_APP_API_URL}/organismes/autocomplete/${formData.organisme}`);
          const organismeData = await organismeSearchResponse.json();

          if (organismeData.status !== 'success' || !organismeData.data.length) {
            throw new Error("Impossible de trouver l'organisme sélectionné");
          }

          const organismeId = organismeData.data[0].id;

          // 3. Création de l'entrée acheteur
          const acheteurResponse = await fetch(`${process.env.REACT_APP_API_URL}/acheteurs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${registerData.token}`
            },
            body: JSON.stringify({
              id_organisme: organismeId,
              id_user: registerData.user.id,
              nom: formData.nom,
              prenom: formData.prenom,
              telephone: formData.telephone,
              email_contact: formData.email
            }),
          });

          const acheteurData = await acheteurResponse.json();

          if (acheteurData.status !== 'success') {
            throw new Error(acheteurData.message || "Erreur lors de la création du profil acheteur");
          }

          // Succès final
          alert("Inscription réussie ! Votre compte est en attente de validation par l'administrateur de votre organisme.");
          navigate('/login');

        } else {
          // Cas nouvel organisme

          // Validation supplémentaire
          if (!formData.numero_voie || !formData.type_voie || !formData.nom_voie ||
            !formData.departement || !formData.code_postal || !formData.ville ||
            !formData.telephone || !formData.prenom) {
            throw new Error("Tous les champs sont obligatoires.");
          }

          // 1. Création de l'adresse
          const adresseResponse = await fetch(`${process.env.REACT_APP_API_URL}/adresses`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              numero_voie: formData.numero_voie,
              type_voie: formData.type_voie,
              nom_voie: formData.nom_voie,
              complement: '',
              code_postal: formData.code_postal,
              ville: formData.ville,
              departement: formData.departement,
              pays: 'France'
            }),
          });

          const adresseData = await adresseResponse.json();
          if (adresseData.status !== 'success') {
            throw new Error(adresseData.message || "Erreur lors de la création de l'adresse");
          }

          // 2. Création de l'organisme
          const organismeResponse = await fetch(`${process.env.REACT_APP_API_URL}/organismes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              nom: formData.organisme,
              id_adresses: adresseData.data.id,
              siret: formData.siret,
              type: 'Public'
            }),
          });

          const organismeData = await organismeResponse.json();
          if (organismeData.status !== 'success') {
            throw new Error(organismeData.message || "Erreur lors de la création de l'organisme");
          }

          // 3. Inscription de l'utilisateur
          const registerResponse = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
              password_confirmation: formData.password_confirmation,
              telephone: formData.telephone,
              role: 'responsable_achat'
            }),
          });

          const registerData = await registerResponse.json();
          if (registerData.status !== 'success') {
            throw new Error(registerData.message || "Erreur lors de l'inscription");
          }

          // 4. Création du responsable d'achat
          const responsableResponse = await fetch(`${process.env.REACT_APP_API_URL}/responsables-achat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${registerData.token}`
            },
            body: JSON.stringify({
              id_organisme: organismeData.data.id,
              id_user: registerData.user.id,
              nom: formData.nom,
              prenom: formData.prenom,
              telephone: formData.telephone,
              email_contact: formData.email
            }),
          });

          const responsableData = await responsableResponse.json();
          if (responsableData.status !== 'success') {
            throw new Error(responsableData.message || "Erreur lors de la création du profil responsable d'achat");
          }

          // Succès final
          alert("Inscription réussie ! Votre compte est en attente de validation par l'équipe Centralis.");
          navigate('/login');
        }
      } else {
        // Logique pour les prestataires
        if (!prestataireSouscription.entite || !prestataireSouscription.email) {
          throw new Error("Veuillez renseigner le nom de l'entité et l'email.");
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/prestataires/souscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(prestataireSouscription),
        });

        const data = await response.json();

        if (data.status !== 'success') {
          throw new Error(data.message || 'Une erreur est survenue lors de la souscription');
        }

        alert("Merci pour votre souscription ! Vous serez informé lors de la prochaine phase de candidature.");
        navigate('/login');
      }
    } catch (error) {
      console.error('Register error:', error);
      setError(error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'inscription');
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour vérifier le mot de passe en direct
  const validatePassword = (password: string) => {
    setPasswordValidation({
      hasLowerCase: /[a-z]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      isLongEnough: password.length >= 8,
      notEmail: !/\S+@\S+\.\S+/.test(password) // Vérifie que le mot de passe n'est pas une adresse email
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let newValue = value;
    // For telephone field, remove non-digit characters and limit length to 10
    if (name === 'telephone') {
      newValue = value.replace(/\D/g, '');
      if (newValue.length > 10) {
        newValue = newValue.slice(0, 10);
      }
    }

    // Mise à jour de tous les champs du formulaire y compris acceptCGV
    setFormData(prev => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : newValue
    }));

    // Valider le mot de passe en direct
    if (name === 'password') {
      validatePassword(value);
    }

    // Mettre à jour l'adresseInput quand un des champs d'adresse est modifié
    if (['numero_voie', 'type_voie', 'nom_voie', 'code_postal', 'ville'].includes(name)) {
      // Construire une nouvelle adresse formatée à partir des champs du formulaire
      const updatedFormData = {
        ...formData,
        [name]: value
      };

      const adresseFormatee = [
        updatedFormData.numero_voie,
        updatedFormData.type_voie,
        updatedFormData.nom_voie,
        updatedFormData.code_postal,
        updatedFormData.ville
      ].filter(Boolean).join(' ');

      setAdresseInput(adresseFormatee);
    }
  };

  const handlePrestataireSouscriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPrestataireSouscription(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectOrganisme = (organismeNom: string) => {
    setFormData(prev => ({ ...prev, organisme: organismeNom }));
    setOrganismeOptions([]);
    setOrganismeExists(true);
  };

  // Nouveau: Sélection d'une adresse depuis les suggestions IGN
  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    const type = detectTypeVoie(suggestion.street);
    let nomVoie = suggestion.street || '';

    // Retirer le type de voie du nom de la voie
    if (type && nomVoie) {
      // Nettoyer le nom de voie en retirant le type au début
      const typeRegex = new RegExp(`^${type}\\s+`, 'i');
      nomVoie = nomVoie.replace(typeRegex, '');
    }

    setFormData(prev => ({
      ...prev,
      numero_voie: suggestion.houseNumber || '',
      nom_voie: nomVoie,
      code_postal: suggestion.postcode || '',
      ville: suggestion.city || '',
      departement: suggestion.postcode ? suggestion.postcode.slice(0, 2) : '',
      type_voie: type
    }));

    setAdresseInput(suggestion.label);
    setAdresseSuggestions([]);
  };

  // Ajouter cette fonction au début du composant Register
  const fetchWithoutAuth = async (url: string, options: RequestInit = {}) => {
    // Assurez-vous que les en-têtes Authorization ne sont pas inclus
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };

    // Filtrer explicitement pour supprimer Authorization s'il existe
    delete (headers as any)['Authorization'];

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const handleTelephoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const tel = formData.telephone;
    if (tel.length !== 10 || (!tel.startsWith('06') && !tel.startsWith('07'))) {
      setTelError("Le numéro de téléphone doit contenir 10 chiffres et débuter par 06 ou 07.");
    } else {
      setTelError('');
    }
  };

  // Vérification de la correspondance des emails
  useEffect(() => {
    if (formData.confirm_email !== '' && formData.email !== formData.confirm_email) {
      setEmailError("Les adresses email ne correspondent pas.");
    } else {
      setEmailError('');
    }
  }, [formData.email, formData.confirm_email]);

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="logo-container">
          <LogoIcon className="logo" />
        </div>
        <h1>Inscription</h1>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Question Acheteur */}
        <div className="form-group si-acheteur">
          <label>Êtes-vous un acheteur ?<span style={{ color: 'red' }}>*</span></label>
          <div className="bouton-radio">
            <label>
              <input
                type="radio"
                name="isAcheteur"
                checked={isAcheteur === true}
                onChange={() => setIsAcheteur(true)}
                required
              />
              Oui
            </label>
            <label>
              <input
                type="radio"
                name="isAcheteur"
                checked={isAcheteur === false}
                onChange={() => setIsAcheteur(false)}
                required
              />
              Non
            </label>
          </div>
        </div>

        {/* Affichage conditionnel en fonction de la réponse à "Êtes-vous un acheteur?" */}
        {isAcheteur === false && (
          <div className="prestataire-section">
            <div className="info-message">
              <p>Vous ne pouvez pas vous inscrire si vous n'êtes pas un acheteur.</p>
              <p>Si vous êtes prestataire, les candidatures se font tous les deux ans. Pour être tenu au courant de notre prochaine phase de candidature, renseignez votre email :</p>
            </div>
            <form onSubmit={handleSubmit} className="prestataire-form">
              <div className="form-group">
                <label htmlFor="entite">Nom de l'entité<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  id="entite"
                  name="entite"
                  value={prestataireSouscription.entite}
                  onChange={handlePrestataireSouscriptionChange}
                  placeholder="Nom de votre entreprise"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="email"
                  id="email-prestataire"
                  name="email"
                  value={prestataireSouscription.email}
                  onChange={handlePrestataireSouscriptionChange}
                  placeholder="exemple@entreprise.com"
                  required
                />
              </div>

              {cgvError && <div className="cgv-error">{cgvError}</div>}

              <div className="form-actions-container">
                <button 
                  type="button"
                  className="cgv-view-button"
                  onClick={() => setIsCGVModalOpen(true)}
                >
                  Accepter les CGV
                </button>

                <Button
                  type="submit"
                  variant="primary"
                  value={isLoading ? "Souscription en cours..." : "S'inscrire"}
                  size="medium"
                  disabled={isLoading}
                  className="submit-button"
                />
              </div>
            </form>
          </div>
        )}

        {/* Formulaire pour les acheteurs */}
        {isAcheteur === true && (
          <form onSubmit={handleSubmit} className="acheteur-form">
            {/* Choix de l'organisme */}
            <div className="form-group">
              <label htmlFor="organisme">
                Organisme<span style={{ color: 'red' }}>*</span>
              </label>
              <div className="autocomplete-container">
                <input
                  type="text"
                  id="organisme"
                  name="organisme"
                  value={formData.organisme}
                  onChange={handleChange}
                  placeholder="Nom de votre organisme"
                  required
                  autoComplete="off"
                  className="organisme-input"
                />
                <div className="icon-container">
                  {isOrganismeSearchLoading ? (
                    <div className="search-loading"></div>
                  ) : (
                    <SearchIcon className="search-icon" />
                  )}
                </div>
                {organismeOptions.length > 0 && (
                  <ul className="autocomplete-list">
                    {organismeOptions.map(org => (
                      <li key={org.id} onClick={() => handleSelectOrganisme(org.nom)}>
                        {org.nom}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Champs supplémentaires si l'organisme n'existe pas */}
            {organismeExists === false && (
              <div className="nouveau-organisme-section">
                <h3>Informations sur l'organisme</h3>

                {/* Champ d'autocomplétion d'adresse IGN */}
                <div className="form-group">
                  <label htmlFor="adresse-autocomplete">Recherche d'adresse<span style={{ color: 'red' }}>*</span></label>
                  <div className="autocomplete-container">
                    <input
                      type="text"
                      id="adresse-autocomplete"
                      value={adresseInput}
                      onChange={(e) => setAdresseInput(e.target.value)}
                      placeholder="Saisissez l'adresse pour l'autocompléttion"
                      autoComplete="off"
                    />
                    {adresseSuggestions.length > 0 && (
                      <ul className="autocomplete-list" role="listbox">
                        {adresseSuggestions.map((suggestion, index) => (
                          <li
                            key={index}
                            role="option"
                            onClick={() => handleSelectAdresse(suggestion)}
                          >
                            {suggestion.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="departement">Département<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="departement"
                      name="departement"
                      value={formData.departement}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="code_postal">Code postal<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="code_postal"
                      name="code_postal"
                      value={formData.code_postal}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ville">Ville<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="ville"
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="numero_voie">Numéro<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="numero_voie"
                      name="numero_voie"
                      value={formData.numero_voie}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="type_voie">Type de voie<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="type_voie"
                      name="type_voie"
                      value={formData.type_voie}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="nom_voie">Nom de la voie<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    id="nom_voie"
                    name="nom_voie"
                    value={formData.nom_voie}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="siret">SIRET<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    id="siret"
                    name="siret"
                    value={formData.siret || ''}
                    onChange={handleChange}
                    placeholder="Ex : 12345678901234"
                    required
                    pattern="^\d{14}$"
                    maxLength={14}
                    minLength={14}
                    title="Le SIRET doit comporter exactement 14 chiffres"
                  />
                </div>
              </div>
            )}

            {/* Informations de l'utilisateur (titre modifié en fonction de l'existence de l'organisme) */}
            <div className="utilisateur-section">
              <h3>{organismeExists === true ? "Informations de l'utilisateur" : "Informations du responsable du compte"}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nom">Nom<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="prenom">Prénom<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    placeholder="Votre prénom"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="exemple@email.com"
                    required
                  />
                </div>
                {/* Champ de confirmation d'email */}
                <div className="form-group">
                  <label htmlFor="confirm_email">Confirmer l'email<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="email"
                    id="confirm_email"
                    name="confirm_email"
                    value={formData.confirm_email}
                    onChange={handleChange}
                    placeholder="Confirmez votre email"
                    required
                    className={emailError ? 'input-error' : ''}
                  />
                  {emailError && <span className="error-message">{emailError}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="telephone">Téléphone<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 23 00 00 00"
                    required
                    onBlur={handleTelephoneBlur}
                    className={telError ? 'input-error' : ''}
                  />
                  {telError && <span className="error-message">{telError}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Mot de passe<span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Entrez votre mot de passe"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                <div className="password-requirements">
                  <p>Le mot de passe doit contenir :</p>
                  <ul>
                    <li className={passwordValidation.hasLowerCase ? 'valid' : 'invalid'}>Une minuscule</li>
                    <li className={passwordValidation.hasUpperCase ? 'valid' : 'invalid'}>Une majuscule</li>
                    <li className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>Un chiffre</li>
                    <li className={passwordValidation.hasSpecialChar ? 'valid' : 'invalid'}>Un caractère spécial</li>
                    <li className={passwordValidation.isLongEnough ? 'valid' : 'invalid'}>Au moins 8 caractères</li>
                    <li className={passwordValidation.notEmail ? 'valid' : 'invalid'}>Ne doit pas être une adresse email</li>
                  </ul>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password_confirmation">Confirmer le mot de passe<span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    placeholder="Confirmez votre mot de passe"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                {!passwordsMatch && formData.password_confirmation !== '' && (
                  <div className="error-message">
                    Les mots de passe ne correspondent pas.
                  </div>
                )}
              </div>
            </div>

            {cgvError && <div className="cgv-error">{cgvError}</div>}

            <div className="form-actions-container">
              <button 
                type="button"
                className="cgv-view-button"
                onClick={() => setIsCGVModalOpen(true)}
              >
                Accepter les CGV
              </button>

              <Button
                type="submit"
                variant="primary"
                value={isLoading ? "Inscription en cours..." : "S'inscrire"}
                size="medium"
                disabled={isLoading || !formData.acceptCGV}
                className="submit-button"
              />
            </div>
          </form>
        )}

        {/* Modal d'affichage des CGV */}
        <CGVAccept
          acceptCGV={formData.acceptCGV}
          setAcceptCGV={(newAccept: boolean) =>
            setFormData(prev => ({ ...prev, acceptCGV: newAccept }))
          }
          isOpen={isCGVModalOpen}
          onClose={() => setIsCGVModalOpen(false)}
          onConfirm={() => {
            // Lorsque l'utilisateur confirme l'acceptation des CGV
            setFormData(prev => ({ ...prev, acceptCGV: true }));
            setIsCGVModalOpen(false);
          }}
          className="cgv-modal-large"
        />

        <div className="register-footer">
          <p className="login-link">
            Déjà inscrit ? <Link to="/login">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;